import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import StepShell from "../StepShell";

const FIXED_GUARDRAILS = [
  { id: "nao_da_conselho_juridico", label: "Não dá conselho jurídico" },
  { id: "nao_fala_preco", label: "Não fala preço" },
  { id: "nao_promete_resultado", label: "Não promete resultado" },
  { id: "respostas_curtas_25_35", label: "Respostas curtas (25–35 palavras)" },
];

const SUGGESTED_GUARDRAILS = [
  { id: "confirma_area_antes_de_qualificar", label: "Confirma a área antes de qualificar" },
  { id: "encaminha_urgencias_de_prazo", label: "Encaminha urgências de prazo/audiência na hora" },
  { id: "nao_pede_dados_sensiveis_desnecessarios", label: "Não pede dados sensíveis sem necessidade" },
  { id: "nao_promete_prazos_processuais", label: "Não promete prazos processuais" },
  { id: "tom_empatico_em_casos_delicados", label: "Mantém tom empático em casos delicados" },
  { id: "nunca_se_passa_por_advogado", label: "Nunca se passa por advogado(a)" },
];

/**
 * Regras do atendente virtual — guardrails fixas (sempre ativas, só confirmar)
 * e regras extras recomendadas do universo jurídico.
 */
export default function IdentidadeRegras({ briefing, autosave }) {
  const [extra, setExtra] = useState(briefing.identity?.extraGuardrails || []);
  const fixedIds = FIXED_GUARDRAILS.map((g) => g.id);

  function toggleExtra(id) {
    const next = extra.includes(id) ? extra.filter((g) => g !== id) : [...extra, id];
    setExtra(next);
    autosave({ identity: { fixedGuardrails: fixedIds, extraGuardrails: next } });
  }

  return (
    <StepShell
      title="Quais regras o assistente deve seguir?"
      description="As regras fixas já vêm ligadas. Reforce com as regras extras que fizerem sentido para o escritório."
    >
      <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
        <Lock className="h-4 w-4 text-muted-foreground" /> Regras fixas (sempre ativas)
      </h3>
      <div className="flex flex-wrap gap-2">
        {FIXED_GUARDRAILS.map((g) => (
          <span
            key={g.id}
            className="rounded-full border border-border bg-surface-inset/60 px-3.5 py-2 text-sm text-muted-foreground"
          >
            {g.label}
          </span>
        ))}
      </div>

      <h3 className="mb-3 mt-7 flex items-center gap-2 text-sm font-medium text-foreground">
        <ShieldCheck className="h-4 w-4 text-cyan-600 dark:text-cyan-400" /> Regras extras
        (recomendadas)
      </h3>
      <div className="flex flex-wrap gap-2">
        {SUGGESTED_GUARDRAILS.map((g) => {
          const active = extra.includes(g.id);
          return (
            <motion.button
              key={g.id}
              type="button"
              onClick={() => toggleExtra(g.id)}
              whileTap={{ scale: 0.96 }}
              className={cn(
                "rounded-full border px-3.5 py-2 text-sm transition-colors",
                active
                  ? "border-cyan-500/70 bg-cyan-500/10 font-medium text-cyan-600 dark:text-cyan-400"
                  : "border-border bg-surface text-muted-foreground hover:border-border-strong",
              )}
            >
              {g.label}
            </motion.button>
          );
        })}
      </div>
    </StepShell>
  );
}
